import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { forkJoin, skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { GstSearchService } from '../../../services/gst/gst.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-osint-gst-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './osint-gst-search.component.html',
  styleUrls: ['./osint-gst-search.component.css'],
})
export class OsintGstSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;
  singleGst: string = '';

  textAreaInput: string = '';
  multipleGsts: string[] = [];
  extraGsts: Array<{ value: string }> = [];
  readonly maxSearchRows = 5;

  isMultipleSearch: boolean = false;

  allJobResultsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000; // 1 second

  searchType: string = 'gst';
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  ipAddress: string = '';
  location: string = '';

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;

  recentResultsSelectAll = { isSelected: false };

  selectedFilterType: string = 'gst';
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  /** Matches backend GSTIN format (15 chars). */
  private readonly gstinPattern =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  filterTypes: any = [
    { label: 'GSTIN', value: 'gst' },
    // { label: 'Status', value: 'status' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  gstFilter: string = '';
  statusFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.gst_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private gstSearchService: GstSearchService,
    private searchPollingService: SearchPollingService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) {}

  ngOnInit(): void {
    this.accessDetails = this.retrieveAccessDetails();

    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = searchServices.gst === false;
      }
    });
    this.searchServiceStatusService.refreshSearchServices();

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'gstSearchCreditCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    // Check for auto-search from navigation
    const autoSearchState = history.state;
    if (autoSearchState?.autoSearch && autoSearchState?.searchQuery) {
      this.singleGst = autoSearchState.searchQuery;
      // Clear the auto-search state to prevent re-triggering on back navigation
      history.replaceState(
        { ...history.state, autoSearch: false, searchQuery: null },
        '',
      );
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.onSearchSingleGstButtonClick();
      }, 100);
    }
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        // console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => {},
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.gstSearchService
      .getGstSearchHistory(
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.gst_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) =>
        recentResultPaged.gst_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.gstSearchService
      .deleteGstSearchHistory({
        gst_search_ids: this.selectedRecentResults,
      })
      .subscribe({
        next: () => {
          this.recentResultsSelectAll.isSelected = false;

          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();

          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.gst_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
      } else {
        if (this.recentResultsHasPreviousPage) {
          this.recentResultsCurrentPage--;
        }
      }
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.gstFilter = '';
    this.statusFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  onMultipleSearchChange(): void {
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singleGst.trim()) {
        this.textAreaInput = this.singleGst.trim();
      }
      return;
    }
    if (!this.singleGst.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singleGst = first;
      }
    }
  }

  resetSearchInputs() {
    this.singleGst = '';
    this.textAreaInput = '';
    this.multipleGsts = [];
    this.extraGsts = [];
    this.isMultipleSearch = false;
  }

  onSearchSingleGstButtonClick() {
    if (this.isServiceDisabled) {
      this.toastr.error('GST search is currently unavailable');
      return;
    }
    const values = this.collectGstValues();
    if (!values.length) {
      this.toastr.error('Please enter a GSTIN');
      return;
    }
    if (values.length > this.maxSearchRows) {
      this.toastr.error('You can only search a maximum of 5 GSTINs at once.');
      return;
    }
    if (!values.every((gstin) => this.validateGstin(gstin))) {
      this.toastr.error(
        values.length > 1
          ? 'One or more GSTINs are invalid'
          : 'GSTIN is invalid',
      );
      return;
    }
    if (this.isThrottled()) {
      return;
    }
    this.isMultipleSearch = values.length > 1;
    this.multipleGsts = values;
    this.getJobIds();
  }

  get canAddSearchRow(): boolean {
    return this.extraGsts.length < this.maxSearchRows - 1;
  }

  addSearchRow(): void {
    if (!this.canAddSearchRow) {
      this.toastr.error('You can only search a maximum of 5 GSTINs at once.');
      return;
    }
    this.extraGsts = [...this.extraGsts, { value: '' }];
    this.isMultipleSearch = this.extraGsts.length > 0;
  }

  removeSearchRow(index: number): void {
    this.extraGsts = this.extraGsts.filter((_, i) => i !== index);
    this.isMultipleSearch = this.extraGsts.length > 0;
  }

  private collectGstValues(): string[] {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singleGst];
    return rawValues
      .map((value) =>
        String(value || '')
          .trim()
          .replace(/\s+/g, '')
          .toUpperCase(),
      )
      .filter((value) => value.length > 0);
  }

  private isThrottled(): boolean {
    const now = Date.now();
    const timeSinceLastSearch = now - this.lastSearchTime;

    return timeSinceLastSearch < this.SEARCH_THROTTLE_MS;
  }

  getJobIds() {
    const { ip, location } = this.retrieveIPAndLocation();
    const values = this.multipleGsts.length
      ? this.multipleGsts.map((e) =>
          e.trim().replace(/\s+/g, '').toUpperCase(),
        )
      : [this.singleGst.trim().replace(/\s+/g, '').toUpperCase()];

    this.loadingJobIds = true;
    this.lastSearchTime = Date.now();

    const requests = values.map((gstin) => {
      const payload: any = {
        gstin,
        device: 'Chrome',
      };
      if (ip) payload.ip_address = ip;
      if (location) payload.location = location;
      return this.gstSearchService.searchGst(payload);
    });

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        const jobIds: string[] = [];
        const bgJobs: Array<{
          job_id: string;
          searchType: string;
          label: string;
        }> = [];

        responses.forEach((response, index) => {
          const jobId = response?.data?.job_id;
          if (jobId) {
            jobIds.push(jobId);
            bgJobs.push({
              job_id: jobId,
              searchType: 'gst',
              label: String(values[index] ?? jobId),
            });
          }
        });

        this.jobIds = jobIds;

        if (jobIds.length) {
          this.searchPollingService.startPolling(this.jobIds, (ids) =>
            this.gstSearchService.getSearchResults(ids),
          );
          this.searchPollingService.ensureBackgroundPolling(bgJobs, 3 * 1000);
          this.getAllJobResults();
        }

        this.reset();
        this.resetSearchInputs();
        this.getRecentResults();

        this.loadingJobIds = false;
      },
      error: (error) => {
        console.error(error);
        this.loadingJobIds = false;
        if (error?.status === 503) {
          this.isServiceDisabled = true;
        }

        let errorMessage = 'An error occurred while processing your request';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Search Failed');
      },
    });
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter(
        (result: any) =>
          result.job_id &&
          (!result.done ||
            result.done === false ||
            result.status === 'pending'),
      )
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.gstSearchService.getSearchResults(jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  getAllJobResults() {}

  extractJobIds(jobs: any[]) {
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }

    if (jobs.length > 0 && typeof jobs[0] === 'string') {
      return jobs;
    }

    return jobs.filter((job) => job.done === false).map((job) => job.job_id);
  }

  /** Matches backend GSTIN format (15 chars). */
  validateGstin(gstin: string) {
    const normalized = gstin.trim().replace(/\s+/g, '').toUpperCase();
    return this.gstinPattern.test(normalized);
  }

  extractMultipleGsts() {
    this.multipleGsts = this.textAreaInput
      .split('\n')
      .map((gstin) => gstin.trim().replace(/\s+/g, '').toUpperCase())
      .filter((gstin) => gstin.length > 0);
  }

  validateGsts() {
    return this.multipleGsts.every((gstin) => this.validateGstin(gstin));
  }

  onSearchMultipleGstsButtonClick() {
    this.extractMultipleGsts();

    if (this.validateGsts()) {
      if (this.isThrottled()) {
        return;
      }
      this.getJobIds();
    } else {
      this.toastr.error('One or more GSTINs are invalid');
    }
  }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate([`/gst-search-result/${searchResult.job_id}`], {
      state: { searchResult },
    });
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onGstFilterChange(gst: string) {
    const trimmedGst = gst.trim();

    if (trimmedGst === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedGst.length === 1) {
      return;
    } else {
      this.filterKey = 'searched_gst';
      this.filterValue = trimmedGst;

      this.getRecentResults();
    }
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;

    if (this.statusFilter === 'all') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.statusFilter === 'success') {
      this.filterKey = 'status';
      this.filterValue = 'success';

      this.getRecentResults();
    }

    if (this.statusFilter === 'pending') {
      this.filterKey = 'status';
      this.filterValue = 'pending';

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'GST SEARCH ID': recentResultPaged.gst_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED GST': recentResultPaged.searched_gst,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `gst-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'GST Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `gst-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'GST Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `gst-results-${timestamp || 'export'}`;
    const title = `GST results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
